import FunctionKeyImage from '@assets/function-key.svg';
import styled from '@emotion/styled';
import { FUNCTION_KEY_MODE } from '@racemap/sdk/schema/trackers';
import { isAccessible } from '@racemap/utilities/functions/trackers';
import type { TableCellProps } from 'react-virtualized';
import { SuccessFailIcon } from 'src/components/BasicComponents/SuccessFailIcon';
import { useCurrentUser } from '../../../../lib/customHooks';
import {
  type ManagedTracker,
  TrackerColumnKeys,
} from '../../../../store/trackers/trackers_reducers';
import { Columns } from '../../../DataTable';

interface CellProps extends TableCellProps {
  cellData?: ManagedTracker['state'];
  rowData: ManagedTracker;
}

export const SOSColumn = () => {
  const currentUser = useCurrentUser();

  const cellRenderer = ({ rowData }: CellProps) => {
    if (rowData == null || !isAccessible(rowData, currentUser)) return null;

    return (
      <SuccessFailIcon isSuccess={rowData.setting?.functionKeyMode === FUNCTION_KEY_MODE.SOS} />
    );
  };

  return Columns.sortingColumn({
    dataKey: TrackerColumnKeys.SOS,
    label: 'SOS',
    cellRenderer,
    minWidth: 70,
    maxWidth: 70,
    columnData: { hiddenOnDefault: true },
    hintWidth: 470,
    hint: (
      <Hint>
        <div>
          SOS message enabled or disabled. Press and hold the function key for 3 seconds to trigger
          an SOS event. The device will send its current location based on the latest GNSS fix, then
          attempt to acquire a new GNSS position. Once the GNSS update completes or times out, the
          device will report the SOS message to RACEMAP. Additional processing may apply depending
          on enabled add-ons.
        </div>
        <picture>
          <img src={FunctionKeyImage} width="80px" alt="Function key mode" />
        </picture>
      </Hint>
    ),
    sortingMethod: (_cellA, _cellB, rowDataA: ManagedTracker, rowDataB: ManagedTracker) => {
      const modeA = rowDataA.setting?.functionKeyMode;
      const modeB = rowDataB.setting?.functionKeyMode;

      if (modeA === modeB) return 0;
      if (modeA === FUNCTION_KEY_MODE.SOS) return -1;
      if (modeB === FUNCTION_KEY_MODE.SOS) return 1;
      return 0;
    },
  });
};

const Hint = styled.div`
  display: flex;
  flex-direction: row;
  width: 450px;
  align-items: center;
  height: 175px;
`;
